'use client';

import React, { useState, useEffect } from 'react';
import { adminStopImpersonation } from '@/app/actions/admin';
import { ShieldAlert, LogOut, Loader2 } from 'lucide-react';

export default function ImpersonationBanner() {
  const [impersonatingData, setImpersonatingData] = useState<{ email: string; adminEmail: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Read session status via lightweight client check
    async function checkSession() {
      try {
        const { getCurrentUserSession } = await import('@/app/actions/auth');
        const session = await getCurrentUserSession();
        if (session && session.impersonatorAdminEmail) {
          setImpersonatingData({
            email: session.email,
            adminEmail: session.impersonatorAdminEmail
          });
        } else {
          setImpersonatingData(null);
        }
      } catch (e) {
        // silent fail
      }
    }
    checkSession();
  }, []);

  if (!impersonatingData) return null;

  const handleStop = async () => {
    setLoading(true);
    try {
      const res = await adminStopImpersonation();
      if (res.success && res.redirectUrl) {
        window.location.href = res.redirectUrl;
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.error('Failed to stop impersonation:', e);
      setLoading(false);
    }
  };

  return (
    <aside aria-label="Administrator Impersonation Notice" className="bg-warning text-black px-4 py-2.5 shadow-xl relative z-[9999] flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm font-medium">
      <div className="flex items-center gap-2.5">
        <ShieldAlert className="w-5 h-5 shrink-0 animate-pulse text-warning" />
        <span>
          <strong>Admin Mode:</strong> You are viewing the platform as{' '}
          <span className="underline font-bold text-black bg-black/10 px-2 py-0.5 rounded">{impersonatingData.email}</span>{' '}
          (Logged in from: {impersonatingData.adminEmail})
        </span>
      </div>
      <button
        onClick={handleStop}
        disabled={loading}
        className="px-3.5 py-1.5 bg-black/80 hover:bg-black text-white font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow border border-white/20 hover:scale-105 active:scale-95 disabled:opacity-50 shrink-0"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
        <span>Exit User & Return to Admin</span>
      </button>
    </aside>
  );
}
